// What a station is running now (F-CAT-04, spec/10 §7.3), on the floor tile, the station sheet and a running
// session: "Playing Dota 2 · 23 min" for a library title (its name, else its exe; the minutes only when the
// station said since when), "Other: chrome" for a foreground program that is no library title, and nothing
// when the station runs nothing or is offline. The words are one line; a tile truncates them with an
// ellipsis and carries the full text in its `title`.

import type { FloorStationState, FloorStationTitle } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { cx } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { useFloor } from './queries';

import './now-playing.css';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const MINUTE_MS = 60_000;

/** The now-playing line for a station's title, or `null` when there is nothing to say. */
export function nowPlayingText(title: FloorStationTitle | null | undefined, now: number, t: Translate): string | null {
  if (title === null || title === undefined) return null;
  const exe = title.exe.trim();
  if (title.titleId === null) return exe === '' ? null : t('floor.playing.other', { exe });
  const name = title.name !== undefined && title.name.trim() !== '' ? title.name.trim() : exe;
  if (name === '') return null;
  const since = title.since === undefined ? Number.NaN : new Date(title.since).getTime();
  if (Number.isNaN(since)) return t('floor.playing.title', { name });
  const minutes = Math.max(0, Math.floor((now - since) / MINUTE_MS));
  if (minutes < 60) return t('floor.playing.titleMinutes', { name, minutes });
  return t('floor.playing.titleHours', { name, hours: Math.floor(minutes / 60), minutes: minutes % 60 });
}

/** The line for a station on the floor: nothing while it is offline. */
export function stationNowPlaying(station: Pick<FloorStationState, 'online' | 'title'> | undefined, now: number, t: Translate): string | null {
  if (station === undefined || !station.online) return null;
  return nowPlayingText(station.title, now, t);
}

/** The now-playing line, one row with a small glyph, truncated with an ellipsis. Renders nothing without text. */
export function NowPlaying({ text, className }: { readonly text: string | null; readonly className?: string }): ReactNode {
  if (text === null) return null;
  return (
    <span className={cx('console-now-playing', className)} title={text}>
      <span className="console-now-playing__dot" aria-hidden="true" />
      <span className="console-now-playing__text">{text}</span>
    </span>
  );
}

/**
 * The now-playing line for one station, read from the venue's floor snapshot (the Floor's own query and cache
 * entry, kept live by the same push) — for a running session outside the Floor. `null` while the floor is
 * not loaded or the station is not on it.
 */
export function useStationNowPlaying(organizationId: string, venueId: string, stationId: string | null | undefined, now: number): string | null {
  const { t } = useTranslation('console');
  const floor = useFloor(organizationId, venueId);
  if (stationId === null || stationId === undefined) return null;
  const station = floor.data?.stations.find((candidate) => candidate.stationId === stationId);
  return stationNowPlaying(station, now, t);
}
